import { useEffect, useCallback, useRef } from 'react';

type FocusableElement = HTMLElement & {
  focus: () => void;
};

// Get all focusable elements in a container
export function getFocusableElements(container: HTMLElement): FocusableElement[] {
  const selector = `
    a[href]:not([disabled]):not([tabindex="-1"]),
    button:not([disabled]):not([tabindex="-1"]),
    input:not([disabled]):not([tabindex="-1"]):not([type="hidden"]),
    select:not([disabled]):not([tabindex="-1"]),
    textarea:not([disabled]):not([tabindex="-1"]),
    [tabindex]:not([tabindex="-1"]):not([disabled])
  `;
  return Array.from(container.querySelectorAll<FocusableElement>(selector));
}

// Focus trap for modals
export function useFocusTrap(isOpen: boolean, containerRef: React.RefObject<HTMLElement | null>) {
  const previousActiveElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    // Store the previously focused element
    previousActiveElement.current = document.activeElement as HTMLElement;

    // Focus the first focusable element in the container
    const focusable = getFocusableElements(containerRef.current);
    if (focusable.length > 0) {
      focusable[0].focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !containerRef.current) return;

      const focusable = getFocusableElements(containerRef.current);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus to the previously focused element
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    };
  }, [isOpen, containerRef]);
}

// Tree navigation hook
type UseTreeNavigationParams = {
  items: Array<{ id: string; type: 'file' | 'folder'; parentId?: string }>;
  selectedId: string | null;
  collapsed: Record<string, boolean>;
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  onOpen?: (id: string) => void;
};

export function useTreeNavigation({
  items,
  selectedId,
  collapsed,
  onSelect,
  onToggle,
  onOpen,
}: UseTreeNavigationParams) {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!selectedId) {
        if (items.length > 0) {
          onSelect(items[0].id);
        }
        return;
      }

      const currentIndex = items.findIndex((item) => item.id === selectedId);
      if (currentIndex === -1) return;

      const currentItem = items[currentIndex];

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (currentIndex < items.length - 1) {
            onSelect(items[currentIndex + 1].id);
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (currentIndex > 0) {
            onSelect(items[currentIndex - 1].id);
          }
          break;

        case 'ArrowRight':
          e.preventDefault();
          if (currentItem.type === 'folder') {
            if (collapsed[currentItem.id]) {
              onToggle(currentItem.id);
            } else if (currentIndex < items.length - 1) {
              // Move to first child
              onSelect(items[currentIndex + 1].id);
            }
          }
          break;

        case 'ArrowLeft':
          e.preventDefault();
          if (currentItem.type === 'folder' && !collapsed[currentItem.id]) {
            onToggle(currentItem.id);
          } else if (currentItem.parentId) {
            // Move to parent
            onSelect(currentItem.parentId);
          }
          break;

        case 'Home':
          e.preventDefault();
          if (items.length > 0) {
            onSelect(items[0].id);
          }
          break;

        case 'End':
          e.preventDefault();
          if (items.length > 0) {
            onSelect(items[items.length - 1].id);
          }
          break;

        case 'Enter':
        case ' ':
          e.preventDefault();
          if (currentItem.type === 'folder') {
            onToggle(currentItem.id);
          } else if (onOpen) {
            onOpen(currentItem.id);
          }
          break;

        default:
          // Type-ahead search
          if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            const char = e.key.toLowerCase();
            const startIndex = currentIndex + 1;

            // Search from current position
            for (let i = 0; i < items.length; i++) {
              const idx = (startIndex + i) % items.length;
              const item = items[idx];
              const name = item.id.split('/').pop()?.toLowerCase() || '';
              if (name.startsWith(char)) {
                onSelect(item.id);
                break;
              }
            }
          }
          break;
      }
    },
    [items, selectedId, collapsed, onSelect, onToggle, onOpen]
  );

  return { handleKeyDown };
}

// Skip link component
export function useSkipLinks() {
  const skipToContent = useCallback(() => {
    const main = document.querySelector('section[aria-label="Code editor"]');
    if (main instanceof HTMLElement) {
      main.tabIndex = -1;
      main.focus();
    }
  }, []);

  const skipToSidebar = useCallback(() => {
    const sidebar = document.querySelector('aside[aria-label="File explorer sidebar"]');
    if (sidebar instanceof HTMLElement) {
      sidebar.tabIndex = -1;
      sidebar.focus();
    }
  }, []);

  const skipToPreview = useCallback(() => {
    const preview = document.querySelector('aside[aria-label="Preview and output panel"]');
    if (preview instanceof HTMLElement) {
      preview.tabIndex = -1;
      preview.focus();
    }
  }, []);

  return { skipToContent, skipToSidebar, skipToPreview };
}

// Panel navigation hook (for switching between sidebar, editor, preview)
export function usePanelNavigation() {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+1: Focus sidebar
      if ((e.metaKey || e.ctrlKey) && e.key === '1') {
        e.preventDefault();
        const sidebar = document.querySelector('aside[aria-label="File explorer sidebar"]');
        if (sidebar instanceof HTMLElement) {
          sidebar.tabIndex = -1;
          sidebar.focus();
        }
      }

      // Cmd+2: Focus editor
      if ((e.metaKey || e.ctrlKey) && e.key === '2') {
        e.preventDefault();
        const editor = document.querySelector('.cm-content');
        if (editor instanceof HTMLElement) {
          editor.focus();
        }
      }

      // Cmd+3: Focus preview
      if ((e.metaKey || e.ctrlKey) && e.key === '3') {
        e.preventDefault();
        const preview = document.querySelector('aside[aria-label="Preview and output panel"]');
        if (preview instanceof HTMLElement) {
          preview.tabIndex = -1;
          preview.focus();
        }
      }

      // Escape: Focus editor from anywhere
      if (e.key === 'Escape' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        const activeTag = document.activeElement?.tagName.toLowerCase();
        const isInModal = document.querySelector('.modal-backdrop');
        const isInInput = activeTag === 'input' || activeTag === 'textarea' || activeTag === 'select';

        if (!isInModal && !isInInput) {
          const editor = document.querySelector('.cm-content');
          if (editor instanceof HTMLElement) {
            editor.focus();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
}
